import * as React from 'react';
import { useIntl } from 'react-intl';

import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { AbnormalFlagsDisplay, localizeCodenames } from 'Routes/Examination/utils';
import { examination } from 'neuro-schemas';

const { examinationsList, abnormalFlagsWithDescription } = examination.config;

const opts = examinationsList.map(exa => exa.codeName);

const ExaminationForm = ({ formData, view, initData }: IOwnProps): React.JSX.Element => {
  const { document, onChange } = formData;

  const onChangeCodename = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (onChange) onChange({ codeName: value, code: null });
  };

  React.useEffect(() => {
    // On mount initialize formData with initData
    if (onChange && initData) {
      const initValues: TOnChangeValues = {};
      Object.keys(initData).forEach(key => {
        const k = key as keyof typeof formData.document;
        if (!formData.document[k]) initValues[k] = initData[k];
      });
      onChange(initValues);
    }
  }, []);

  React.useEffect(() => {
    // This is needed to update the code field as well when changing the codeName, especially when the form is supplied with initData
    const code = examinationsList.find(ex => ex.codeName === formData.document.codeName)?.code;
    if (onChange && !formData.document.code && code) onChange({ code: code });
  }, [formData.document.codeName]);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!view?.viewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      <FormRow title="examination.codeName">
        <>
          {!view?.viewing ? (
            <InputHandler
              type="AutoCompleteSelect"
              editing={!initData?.codeName && !view?.viewing}
              name="codeName"
              formData={{
                onChange: onChangeCodename,
                document: { codeName: document.codeName },
              }}
              options={opts}
              getOptionLabel={(o: string): string => (o ? localizeCodenames(o, fm) : '')}
              width={23}
              placeholder={'examination.codeName'}
              groupID="group"
            />
          ) : (
            <div style={{ fontWeight: 600 }}>{document.codeName && localizeCodenames(document.codeName, fm)}</div>
          )}
        </>
      </FormRow>
      <FormRow title="examination.code">
        <div style={{ fontWeight: 600 }}>
          {formData.document.code ? localizeCodenames(formData.document.code, fm) : '-'}
        </div>
      </FormRow>
      <FormRow title="examination.value">
        <InputHandler
          type="TextField"
          editing={!view?.viewing}
          name="value"
          formData={formData}
          placeholder={'examination.value'}
        />
      </FormRow>
      <FormRow title="examination.units">
        <InputHandler
          type="TextField"
          editing={!view?.viewing}
          name="units"
          formData={formData}
          placeholder={'examination.units'}
        />
      </FormRow>
      <FormRow title="examination.valueReferenceRange">
        <InputHandler
          type="TextField"
          editing={!view?.viewing}
          name="valueReferenceRange"
          formData={formData}
          placeholder={'examination.valueReferenceRange'}
        />
      </FormRow>
      <FormRow title="examination.abnormalFlags">
        {view?.viewing ? (
          <div style={{ fontWeight: 600 }}>
            <AbnormalFlagsDisplay abnormalFlags={formData.document.abnormalFlags} fm={fm} />
          </div>
        ) : (
          <InputHandler
            type="AutoCompleteSelect"
            editing={!view?.viewing}
            name="abnormalFlags"
            formData={formData}
            placeholder={'examination.abnormalFlags'}
            options={abnormalFlagsWithDescription}
            width={30}
            groupID="group"
            getOptionLabel={(o: string | undefined) => {
              if (o && abnormalFlagsWithDescription.includes(o))
                return o + ' - ' + fm(`examination.abnormalFlagsDescription.` + o);
              return o ?? '';
            }}
            //multiple={true}
          />
        )}
      </FormRow>
      <FormRow title="examination.comments">
        <InputHandler
          type="TextField"
          editing={!view?.viewing}
          name="comments"
          formData={formData}
          placeholder={'examination.comments'}
        />
      </FormRow>
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'examination'>>;
  view?: IView;
  initData?: Partial<IExamination>;
}

export default ExaminationForm;
